// ==========================================
// src/pages/PainelEspera.jsx
// PAINEL DE ESPERA - BORAFRETE MODERNO
// ✅ Visual moderno com cores BoraFrete
// ✅ Sem valor da carga visível
// ✅ Redireciona para /pagamento-mercadopago
// ✅ PROTEÇÃO: Garante carga como objeto antes de navegar
// ==========================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { 
  Clock, CheckCircle, XCircle, Loader2, Truck, MapPin, 
  ArrowLeft, RefreshCw, Shield, Package, Route,
  AlertTriangle, Timer, Zap, Star, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const TEMPO_ANALISE_SEGUNDOS = 600; // 10 minutos

const PainelEspera = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [consulta, setConsulta] = useState(null);
  const [tempoRestante, setTempoRestante] = useState(TEMPO_ANALISE_SEGUNDOS);
  const [status, setStatus] = useState('aguardando');
  const [loading, setLoading] = useState(true);
  const [progresso, setProgresso] = useState(100);

  const carregarConsulta = useCallback(async () => {
    const consultaId = location.state?.consultaId || localStorage.getItem('borafrete_consulta_atual');
    
    if (!consultaId) {
      toast.error('Nenhuma consulta ativa encontrada');
      navigate('/buscar-cargas');
      return;
    }

    try {
      setLoading(true);
      
      let consultaData = null;
      let cargaData = null;
      
      try {
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('motorista_buscar_consulta', {
            p_consulta_id: consultaId,
            p_motorista_id: user?.id || null
          });

        if (!rpcError && rpcData && rpcData.length > 0) {
          consultaData = rpcData[0];
        }
      } catch (rpcErr) {
        console.warn('Erro RPC:', rpcErr);
      }

      if (!consultaData) {
        const consultas = JSON.parse(localStorage.getItem('borafrete_consultas') || '[]');
        const consultaLocal = consultas.find(c => c.id === consultaId);
        if (consultaLocal) consultaData = consultaLocal;
      }

      if (!consultaData && location.state?.consulta) {
        consultaData = location.state.consulta;
      }

      if (!consultaData) {
        toast.error('Consulta não encontrada');
        navigate('/buscar-cargas');
        return;
      }

      let cargaInfo = consultaData.carga || consultaData.carga_id;
      
      if (typeof cargaInfo === 'string') {
        try {
          const { data: cargaSupabase } = await supabase
            .from('cargas')
            .select('*')
            .eq('id', cargaInfo)
            .single();
          if (cargaSupabase) cargaData = cargaSupabase;
        } catch (e) {
          const cargasLocal = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
          cargaData = cargasLocal.find(c => c.id === cargaInfo);
        }
      } else if (typeof cargaInfo === 'object') {
        cargaData = cargaInfo;
      }

      const consultaCompleta = {
        ...consultaData,
        carga: cargaData || consultaData.carga || {
          titulo: consultaData.carga_titulo || 'Carga',
          origem_cidade: consultaData.carga_origem || 'N/A',
          destino_cidade: consultaData.carga_destino || 'N/A',
          origem_estado: consultaData.carga_origem_estado || '',
          destino_estado: consultaData.carga_destino_estado || '',
          produto: consultaData.carga_tipo || 'Produto',
          peso: consultaData.carga_peso || '',
          peso_unidade: consultaData.carga_peso_unidade || 'kg'
        }
      };

      setConsulta(consultaCompleta);
      setStatus(consultaData.status || 'aguardando');
      
      const criadoEm = new Date(consultaData.created_at || consultaData.data_consulta || Date.now()).getTime();
      const expiraEm = criadoEm + (TEMPO_ANALISE_SEGUNDOS * 1000);
      const agora = Date.now();
      const restante = Math.max(0, Math.floor((expiraEm - agora) / 1000));
      
      setTempoRestante(restante);
      setProgresso((restante / TEMPO_ANALISE_SEGUNDOS) * 100);
      
      if (consultaData.status === 'aprovado') {
        setTimeout(() => irParaPagamento(consultaCompleta), 1500);
      }
    } catch (err) {
      console.error('Erro ao carregar consulta:', err);
      toast.error('Erro ao carregar consulta');
    } finally {
      setLoading(false);
    }
  }, [location.state, navigate, user]);

  useEffect(() => {
    carregarConsulta();
  }, [carregarConsulta]);

  useEffect(() => {
    if (!consulta?.id) return;

    const interval = setInterval(async () => {
      try {
        const { data: rpcData } = await supabase
          .rpc('motorista_buscar_consulta', {
            p_consulta_id: consulta.id,
            p_motorista_id: user?.id || null
          });

        if (rpcData && rpcData.length > 0) {
          const novoStatus = rpcData[0].status;
          
          if (novoStatus !== status) {
            setStatus(novoStatus);
            
            if (novoStatus === 'aprovado') {
              toast.success('🎉 Carga aprovada! Preparando pagamento...', { duration: 3000 });
              setTimeout(() => irParaPagamento(rpcData[0]), 2000);
            } else if (novoStatus === 'recusado') {
              toast.error('❌ Carga não aprovada', { duration: 5000 });
            }
          }
        }
      } catch (e) {
        const consultas = JSON.parse(localStorage.getItem('borafrete_consultas') || '[]');
        const atualizada = consultas.find(c => c.id === consulta.id);
        if (atualizada && atualizada.status !== status) {
          setStatus(atualizada.status);
          if (atualizada.status === 'aprovado') {
            toast.success('🎉 Carga aprovada!', { duration: 3000 });
          } else if (atualizada.status === 'recusado') {
            toast.error('❌ Carga não aprovada', { duration: 5000 });
          }
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [consulta?.id, status, user]);

  useEffect(() => {
    if (tempoRestante <= 0 || status !== 'aguardando') return;
    
    const timer = setInterval(() => {
      setTempoRestante(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          recusarAutomaticamente();
          return 0;
        }
        const novo = prev - 1;
        setProgresso((novo / TEMPO_ANALISE_SEGUNDOS) * 100);
        return novo;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [tempoRestante, status]);

  const recusarAutomaticamente = async () => {
    if (!consulta?.id) return;
    
    try {
      await supabase.rpc('motorista_atualizar_consulta', {
        p_consulta_id: consulta.id,
        p_status: 'recusado',
        p_motivo: 'Tempo de análise expirado (10 minutos)'
      });
    } catch (err) {
      console.warn('RPC falhou:', err);
    }
    
    const consultas = JSON.parse(localStorage.getItem('borafrete_consultas') || '[]');
    const atualizadas = consultas.map(c => 
      c.id === consulta.id 
        ? { ...c, status: 'recusado', motivo_recusa: 'Tempo de análise expirado (10 minutos)', updated_at: new Date().toISOString() }
        : c
    );
    localStorage.setItem('borafrete_consultas', JSON.stringify(atualizadas));
        
    setStatus('recusado');
    toast.error('⏰ Tempo de análise expirado. Tente novamente.', { duration: 6000 });
  };

  // ✅ REDIRECIONA PARA PAGAMENTO MERCADO PAGO COM PROTEÇÃO
  const irParaPagamento = (dadosConsulta = null) => {
    const dados = dadosConsulta || consulta;
    let carga = dados?.carga || dados?.carga_id;
    
    // ✅ PROTEÇÃO 1: Se carga for string (ID), buscar objeto completo
    if (typeof carga === 'string') {
      // Tentar location.state primeiro
      if (location.state?.carga && typeof location.state.carga === 'object') {
        carga = location.state.carga;
      } else {
        // Buscar no localStorage
        const cargasLocal = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
        const cargaEncontrada = cargasLocal.find(c => c.id === carga);
        if (cargaEncontrada) carga = cargaEncontrada;
      }
    }
    
    // ✅ PROTEÇÃO 2: Validar que carga é objeto válido
    if (!carga || typeof carga !== 'object' || !carga.id) {
      toast.error('Erro: dados da carga incompletos');
      return;
    }

    localStorage.removeItem('borafrete_consulta_atual');
    
    // ✅ Salvar no sessionStorage para backup
    sessionStorage.setItem('pagamento_carga', JSON.stringify({
      carga: carga,
      consultaId: dados.id
    }));
    
    // ✅ Navegar com carga completa
    navigate('/pagamento-mercadopago', {
      state: {
        carga: carga,
        consultaId: dados.id,
        motoristaId: user?.id,
        motoristaNome: user?.nome || user?.email,
        motoristaEmail: user?.email,
        fromPainelEspera: true
      }
    });
  };

  const formatarTempo = (segundos) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const cancelarConsulta = async () => {
    if (!consulta?.id) return;
    
    try {
      await supabase.rpc('motorista_atualizar_consulta', {
        p_consulta_id: consulta.id,
        p_status: 'cancelado_motorista',
        p_motivo: 'Cancelado pelo motorista'
      });
    } catch (err) {
      console.warn('RPC falhou:', err);
    }
    
    const consultas = JSON.parse(localStorage.getItem('borafrete_consultas') || '[]');
    const atualizadas = consultas.map(c => 
      c.id === consulta.id 
        ? { ...c, status: 'cancelado_motorista', updated_at: new Date().toISOString() }
        : c
    );
    localStorage.setItem('borafrete_consultas', JSON.stringify(atualizadas));
        
    localStorage.removeItem('borafrete_consulta_atual');
    toast.info('Consulta cancelada');
    navigate('/buscar-cargas');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex flex-col items-center justify-center p-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-4 border-blue-400/30 border-t-orange-500 animate-spin" />
          <Truck className="w-10 h-10 text-orange-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-white text-2xl font-bold mt-8">BoraFrete</p>
        <p className="text-blue-200 text-lg mt-2">Carregando sua consulta...</p>
      </div>
    );
  }

  const carga = consulta?.carga;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      
      {/* Header Moderno */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-blue-100 shadow-sm">
        <div className="flex items-center justify-between max-w-lg mx-auto px-4 py-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/buscar-cargas')} 
            className="text-gray-700 hover:bg-blue-50 h-11 px-3 rounded-xl"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="text-base font-medium">Voltar</span>
          </Button>
          
          <div className="flex items-center gap-2 bg-blue-100 px-3 py-1.5 rounded-full">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
            <span className="text-blue-700 text-sm font-semibold">Online</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">

        {/* Logo/Brand */}
        <div className="text-center mb-2">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2.5 rounded-2xl shadow-lg shadow-blue-200">
            <Truck className="w-6 h-6" />
            <span className="text-xl font-bold">BoraFrete</span>
          </div>
        </div>

        {/* STATUS PRINCIPAL */}
        <div className="text-center space-y-3">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-xl ${
            status === 'aguardando' ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 
            status === 'aprovado' ? 'bg-gradient-to-br from-green-500 to-green-700' : 
            'bg-gradient-to-br from-red-500 to-red-700'
          }`}>
            {status === 'aguardando' && <Clock className="w-10 h-10 text-white animate-pulse" />}
            {status === 'aprovado' && <CheckCircle className="w-10 h-10 text-white" />}
            {status === 'recusado' && <XCircle className="w-10 h-10 text-white" />}
          </div>

          <h1 className="text-2xl font-bold text-gray-900">
            {status === 'aguardando' && 'Aguardando Aprovação'}
            {status === 'aprovado' && 'Carga Aprovada! 🎉'}
            {status === 'recusado' && 'Não Aprovada'}
          </h1>

          <p className="text-base text-gray-500 leading-relaxed px-4">
            {status === 'aguardando' && 'Nossa equipe está analisando sua solicitação. Fique na tela!'}
            {status === 'aprovado' && 'Você será redirecionado para o pagamento em instantes...'}
            {status === 'recusado' && (consulta?.motivo_recusa || 'Entre em contato para mais informações')}
          </p>
        </div>

        {/* TIMER */}
        {status === 'aguardando' && (
          <div className="bg-white rounded-2xl p-5 border border-blue-100 shadow-lg space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Timer className="w-6 h-6 text-blue-600" />
              <span className="text-lg font-semibold text-gray-700">Tempo de Análise</span>
            </div>
            
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-700 font-mono tracking-wider">
                {formatarTempo(tempoRestante)}
              </div>
              <p className="text-sm text-gray-400 mt-1">minutos restantes</p>
            </div>

            <Progress value={progresso} className="h-2.5 bg-gray-100" />
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-700">Não feche esta tela!</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Você será redirecionado automaticamente assim que for aprovado.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* APROVADO */}
        {status === 'aprovado' && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-5 space-y-3 shadow-lg">
            <div className="flex items-center justify-center gap-2">
              <Zap className="w-8 h-8 text-green-600" />
              <span className="text-xl font-bold text-green-700">Tudo certo!</span>
            </div>
            <p className="text-base text-green-600 text-center">
              Preparando seu pagamento seguro...
            </p>
            <div className="flex justify-center">
              <div className="w-10 h-10 border-3 border-green-300 border-t-green-600 rounded-full animate-spin" />
            </div>
          </div>
        )}

        {/* RECUSADO */}
        {status === 'recusado' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300 rounded-2xl p-5 text-center space-y-3 shadow-lg">
              <XCircle className="w-14 h-14 text-red-500 mx-auto" />
              <p className="text-xl font-bold text-red-700">Consulta encerrada</p>
              <p className="text-sm text-red-500">
                {consulta?.motivo_recusa || 'O tempo de análise expirou'}
              </p>
            </div>

            <Button 
              onClick={() => navigate('/buscar-cargas')}
              className="w-full h-14 text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg shadow-blue-200"
            >
              <Truck className="w-6 h-6 mr-2" />
              Buscar Outras Cargas
            </Button>
          </div>
        )}

        {/* DADOS DA CARGA - SEM VALOR */}
        {carga && (
          <div className="bg-white rounded-2xl p-5 space-y-4 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {carga.titulo || carga.produto || 'Carga'}
                </h2>
                <p className="text-sm text-gray-500">Detalhes do transporte</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-base text-gray-700 bg-gray-50 p-3 rounded-xl">
                <Route className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">Rota</p>
                  <p className="font-semibold">
                    {carga.origem_cidade || carga.origem || 'N/A'} - {carga.origem_estado || ''}
                    <span className="text-gray-400 mx-2">→</span>
                    {carga.destino_cidade || carga.destino || 'N/A'} - {carga.destino_estado || ''}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 text-base text-gray-700 bg-gray-50 p-3 rounded-xl">
                <Package className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">Produto</p>
                  <p className="font-semibold">{carga.produto || carga.carga_tipo || 'Não especificado'}</p>
                </div>
              </div>

              {carga.peso && (
                <div className="flex items-center gap-3 text-base text-gray-700 bg-gray-50 p-3 rounded-xl">
                  <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-400 mb-0.5">Peso</p>
                    <p className="font-semibold">{carga.peso} {carga.peso_unidade || 'kg'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Taxa de Agenciamento */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-blue-800">Taxa de Agenciamento</p>
                    <p className="text-xs text-blue-600">Pagamento único e seguro</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-blue-700">R$ 50,00</span>
                  <p className="text-xs text-blue-500">via PIX</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BOTÕES */}
        {status === 'aguardando' && (
          <div className="space-y-3">
            <Button 
              onClick={carregarConsulta}
              variant="outline"
              className="w-full h-12 text-base border-blue-200 text-blue-700 hover:bg-blue-50 rounded-xl"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Atualizar Status
            </Button>

            <Button 
              onClick={cancelarConsulta}
              variant="ghost"
              className="w-full h-11 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancelar Solicitação
            </Button>
          </div>
        )}

        {/* FOOTER */}
        <div className="text-center pt-2 pb-6">
          <div className="inline-flex items-center gap-2 text-gray-400">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <p className="text-xs">
              BoraFrete • Conectando motoristas e empresas
            </p>
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PainelEspera;